import { useEffect, useRef, useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import {
  type ClientSummary,
  createClient,
  deleteClient,
  setClientActive,
  importClientsCsv,
  listClients,
  logout,
  updateClientSections,
} from "../lib/api";

// fix254: derive a favicon URL for a client's website via Google's favicon
// service (handles fetching/caching/fallback server-side, no backend work).
// Returns null if we can't parse a hostname, in which case the avatar shows the
// business-name initial instead.
function faviconUrl(websiteUrl: string, size = 128): string | null {
  try {
    const normalized = /^https?:\/\//i.test(websiteUrl)
      ? websiteUrl
      : `https://${websiteUrl}`;
    const host = new URL(normalized).hostname;
    if (!host) return null;
    return `https://www.google.com/s2/favicons?domain=${encodeURIComponent(host)}&sz=${size}`;
  } catch {
    return null;
  }
}

/**
 * fix268: parse a CSV of clients into { businessName, websiteUrl } rows.
 * - Handles an optional header row (detects columns named name/business and
 *   url/website/site; otherwise assumes column order: name, url).
 * - Tolerates quoted fields and commas inside quotes.
 * - Skips blank lines. Validation/dedup happens server-side.
 */
function parseClientsCsv(
  text: string
): Array<{ businessName: string; websiteUrl: string }> {
  const lines = text
    .replace(/\r\n/g, "\n")
    .replace(/\r/g, "\n")
    .split("\n")
    .filter((l) => l.trim().length > 0);
  if (lines.length === 0) return [];

  const parseLine = (line: string): string[] => {
    const out: string[] = [];
    let cur = "";
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (inQuotes) {
        if (ch === '"' && line[i + 1] === '"') {
          cur += '"';
          i++;
        } else if (ch === '"') {
          inQuotes = false;
        } else {
          cur += ch;
        }
      } else if (ch === '"') {
        inQuotes = true;
      } else if (ch === ",") {
        out.push(cur);
        cur = "";
      } else {
        cur += ch;
      }
    }
    out.push(cur);
    return out.map((c) => c.trim());
  };

  // Detect a header row and column positions.
  let nameIdx = 0;
  let urlIdx = 1;
  let startRow = 0;
  let headerResolved = false;
  const first = parseLine(lines[0]!).map((c) => c.toLowerCase());
  const looksLikeHeader = first.some(
    (c) =>
      c.includes("name") ||
      c.includes("business") ||
      c.includes("url") ||
      c.includes("website") ||
      c.includes("site")
  );
  if (looksLikeHeader) {
    startRow = 1;
    const ni = first.findIndex(
      (c) => c.includes("name") || c.includes("business")
    );
    const ui = first.findIndex(
      (c) => c.includes("url") || c.includes("website") || c.includes("site")
    );
    if (ni >= 0) nameIdx = ni;
    if (ui >= 0) urlIdx = ui;
    headerResolved = true;
  } else {
    // fix271: no header row — auto-detect which column is the URL by content
    // (a value that looks like a domain/link) instead of blindly assuming
    // column order. The name is then the first non-URL, non-empty column.
    const firstRowCols = parseLine(lines[0]!);
    const detectedUrl = firstRowCols.findIndex((c) => looksLikeUrl(c));
    if (detectedUrl >= 0) {
      urlIdx = detectedUrl;
      const detectedName = firstRowCols.findIndex(
        (c, i) => i !== detectedUrl && c.trim().length > 0 && !looksLikeUrl(c)
      );
      nameIdx = detectedName >= 0 ? detectedName : detectedUrl === 0 ? 1 : 0;
      headerResolved = true;
    }
    // If we couldn't detect a URL column, fall through to the default
    // name=0, url=1 assumption (best effort).
  }
  void headerResolved;

  const rows: Array<{ businessName: string; websiteUrl: string }> = [];
  for (let r = startRow; r < lines.length; r++) {
    const cols = parseLine(lines[r]!);
    const businessName = (cols[nameIdx] ?? "").trim();
    const websiteUrl = (cols[urlIdx] ?? "").trim();
    if (!businessName && !websiteUrl) continue;
    rows.push({ businessName, websiteUrl });
  }
  return rows;
}

/**
 * fix271: heuristic — does this CSV cell look like a website URL/domain?
 * Used to auto-detect the URL column in headerless CSVs. Matches things like
 * "https://foo.com", "www.foo.com", or "foo.co.uk" while rejecting plain
 * business names (which rarely contain a dot followed by a TLD with no spaces).
 */
function looksLikeUrl(value: string): boolean {
  const v = value.trim().toLowerCase();
  if (!v) return false;
  if (/^https?:\/\//.test(v)) return true;
  if (/^www\./.test(v)) return true;
  // domain.tld with no spaces (optionally a path). Allows sub.domain.co.uk.
  if (/^[a-z0-9-]+(\.[a-z0-9-]+)+(\/\S*)?$/.test(v) && !v.includes(" ")) {
    return true;
  }
  return false;
}


function ClientAvatar({
  client,
  size = "md",
}: {
  client: ClientSummary;
  size?: "sm" | "md";
}) {
  const [failed, setFailed] = useState(false);
  const initial = client.businessName.charAt(0).toUpperCase();
  const src = faviconUrl(client.websiteUrl);
  const box =
    size === "sm" ? "w-8 h-8 text-sm" : "w-9 h-9";
  const showFavicon = src && !failed;

  return (
    <div
      className={`${box} rounded-md flex items-center justify-center font-semibold shrink-0 overflow-hidden ${
        showFavicon ? "bg-bg-subtle border border-border" : "bg-accent text-white"
      }`}
    >
      {showFavicon ? (
        <img
          src={src}
          alt=""
          loading="lazy"
          className="w-full h-full object-cover"
          onError={() => setFailed(true)}
        />
      ) : (
        initial
      )}
    </div>
  );
}

export default function ClientListPage() {
  const [clients, setClients] = useState<ClientSummary[] | null>(null);
  const [showNewClient, setShowNewClient] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<ClientSummary | null>(null);
  // fix252: tile/list view toggle + sort control. Persisted to localStorage so
  // the preference sticks across visits.
  const [view, setView] = useState<"tile" | "list">(() => {
    const saved = localStorage.getItem("clientListView");
    return saved === "list" ? "list" : "tile";
  });
  const [sortBy, setSortBy] = useState<"alphabetical" | "recent">(() => {
    const saved = localStorage.getItem("clientListSort");
    return saved === "alphabetical" ? "alphabetical" : "recent";
  });
  // fix683: pending checkbox changes, keyed by client id. GBP and Content are
  // now INDEPENDENT per-client flags (both/either/neither) that don't move the
  // client. Priority is the only flag that changes placement. All applied in one
  // batch when Save is clicked.
  const [pendingGbp, setPendingGbp] = useState<Record<number, boolean>>({});
  const [pendingContent, setPendingContent] = useState<Record<number, boolean>>(
    {}
  );
  // fix301: pending Priority toggles, keyed by client id. Applied in the same
  // batch Save.
  const [pendingPriority, setPendingPriority] = useState<
    Record<number, boolean>
  >({});
  const [savingSections, setSavingSections] = useState(false);
  // fix268: CSV import state.
  const [importing, setImporting] = useState(false);
  const [importMsg, setImportMsg] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // fix582: Inactive section is a collapsible accordion, collapsed by default.
  const [inactiveOpen, setInactiveOpen] = useState(false);
  // Clients currently being moved to/from Inactive (disables their button).
  const [activeBusy, setActiveBusy] = useState<Set<number>>(new Set());

  // fix582: move a client to Inactive (active=false) or back into rotation
  // (active=true). Persists immediately (not part of the checkbox Save flow),
  // then refreshes so the client jumps to/from the Inactive section.
  async function handleSetActive(client: ClientSummary, active: boolean) {
    setActiveBusy((prev) => new Set(prev).add(client.id));
    try {
      await setClientActive(client.id, active);
      await refresh();
    } catch (err) {
      console.error("Failed to change client active state:", err);
      alert("Couldn't update the client. Please try again.");
    } finally {
      setActiveBusy((prev) => {
        const next = new Set(prev);
        next.delete(client.id);
        return next;
      });
    }
  }
  const navigate = useNavigate();

  useEffect(() => {
    localStorage.setItem("clientListView", view);
  }, [view]);
  useEffect(() => {
    localStorage.setItem("clientListSort", sortBy);
  }, [sortBy]);

  async function refresh() {
    const list = await listClients();
    setClients(list);
  }

  // fix252: apply the chosen sort. Alphabetical = case-insensitive by name;
  // Recently Added = newest createdAt first (id as a tiebreaker/fallback).
  const sortedClients = (clients ?? []).slice().sort((a, b) => {
    if (sortBy === "alphabetical") {
      return a.businessName.localeCompare(b.businessName, undefined, {
        sensitivity: "base",
      });
    }
    const at = new Date(a.createdAt).getTime();
    const bt = new Date(b.createdAt).getTime();
    if (bt !== at) return bt - at;
    return b.id - a.id;
  });

  useEffect(() => {
    refresh().catch(console.error);
  }, []);

  // fix683: effective flag values = pending change if any, else saved.
  function effectiveGbp(c: ClientSummary): boolean {
    return pendingGbp[c.id] ?? c.gbpChecked ?? false;
  }
  function effectiveContent(c: ClientSummary): boolean {
    return pendingContent[c.id] ?? c.contentChecked ?? false;
  }

  // fix301: the effective Priority flag = pending toggle if any, else saved.
  function effectivePriority(c: ClientSummary): boolean {
    return pendingPriority[c.id] ?? c.isPriority ?? false;
  }

  // fix683: placement is now simple. Inactive overrides everything; otherwise
  // Priority → the Priority section, and everything else → the general "Clients"
  // section. GBP/Content checkboxes never affect placement.
  function effectivePlacement(
    c: ClientSummary
  ): "priority" | "clients" | "inactive" {
    if ((c.workspaceSection ?? "") === "inactive") return "inactive";
    if (effectivePriority(c)) return "priority";
    return "clients";
  }

  // fix683: toggle the GBP or Content checkbox. Independent of each other and of
  // placement. Recorded as pending only when it differs from the saved value.
  function toggleGbp(c: ClientSummary) {
    const nextVal = !effectiveGbp(c);
    setPendingGbp((prev) => {
      const next = { ...prev };
      if (nextVal === (c.gbpChecked ?? false)) delete next[c.id];
      else next[c.id] = nextVal;
      return next;
    });
  }
  function toggleContent(c: ClientSummary) {
    const nextVal = !effectiveContent(c);
    setPendingContent((prev) => {
      const next = { ...prev };
      if (nextVal === (c.contentChecked ?? false)) delete next[c.id];
      else next[c.id] = nextVal;
      return next;
    });
  }

  // fix301: toggle the Priority box. Only recorded as pending when it differs
  // from the saved value.
  function togglePriority(c: ClientSummary) {
    const nextVal = !effectivePriority(c);
    setPendingPriority((prev) => {
      const next = { ...prev };
      if (nextVal === (c.isPriority ?? false)) {
        delete next[c.id]; // back to saved state — no pending change
      } else {
        next[c.id] = nextVal;
      }
      return next;
    });
  }

  // fix683: a client has a pending change if any of its three flags differ from
  // saved. The Save bar / row highlight key off this combined set.
  const pendingIds = new Set<number>([
    ...Object.keys(pendingGbp).map(Number),
    ...Object.keys(pendingContent).map(Number),
    ...Object.keys(pendingPriority).map(Number),
  ]);
  const pendingCount = pendingIds.size;

  async function handleSaveSections() {
    if (pendingCount === 0) return;
    setSavingSections(true);
    try {
      // Build one update per changed client, carrying all three effective flags
      // (the endpoint persists them together).
      const byId = clients ?? [];
      const updates = Array.from(pendingIds).map((id) => {
        const c = byId.find((x) => x.id === id);
        return {
          id,
          gbpChecked: pendingGbp[id] ?? c?.gbpChecked ?? false,
          contentChecked: pendingContent[id] ?? c?.contentChecked ?? false,
          isPriority: pendingPriority[id] ?? c?.isPriority ?? false,
        };
      });
      await updateClientSections(updates);
      setPendingGbp({});
      setPendingContent({});
      setPendingPriority({});
      await refresh(); // refresh so clients move into their new sections
    } catch (err) {
      console.error("Failed to save section changes:", err);
      alert("Could not save changes. Please try again.");
    } finally {
      setSavingSections(false);
    }
  }

  // fix268: parse a CSV (name,url) and import. Tolerant of a header row and of
  // either column order when a header names them.
  async function handleCsvFile(file: File) {
    setImporting(true);
    setImportMsg(null);
    try {
      const text = await file.text();
      const rows = parseClientsCsv(text);
      if (rows.length === 0) {
        setImportMsg("No valid rows found in that CSV.");
        return;
      }
      const result = await importClientsCsv(rows);
      await refresh();
      setImportMsg(
        `Imported ${result.imported} client${result.imported === 1 ? "" : "s"} to Other` +
          (result.skipped > 0
            ? ` · skipped ${result.skipped} (missing data or duplicates)`
            : "")
      );
    } catch (err) {
      console.error("CSV import failed:", err);
      setImportMsg("Import failed. Check the file format and try again.");
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }

  async function handleLogout() {
    try {
      await logout();
    } catch (err) {
      console.error("Logout API call failed:", err);
    } finally {
      // Always navigate, even if the server request errored.
      // The server-side cookie may not be cleared, but the user gets to login screen.
      navigate("/login", { replace: true });
    }
  }

  return (
    <div className="min-h-screen bg-bg-subtle">
      {/* Header */}
      <header className="bg-bg-card border-b border-border">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <h1 className="text-accent font-semibold text-lg">SMC Content App</h1>
            <p className="text-text-subtle text-xs mt-0.5">Internal team workspace</p>
          </div>
          <div className="flex items-center gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) handleCsvFile(f);
              }}
            />
            {/* chunk3z-fix385: Import CSV was hidden per request. fix581:
                re-enabled for bulk client import (name, URL CSV; server skips
                duplicates by URL or name). fix637: hidden again per request —
                the button is gated off but all import machinery (the hidden
                file input above + handleCsvFile) stays intact, so flipping this
                back to `true` re-enables it with no other change. */}
            {false && (
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={importing}
                className="bg-bg-card hover:bg-bg-hover border border-border text-text text-sm rounded-md px-3 py-1.5 transition disabled:opacity-60"
                title="Import clients from a CSV (name, URL)"
              >
                {importing ? "Importing…" : "Import CSV"}
              </button>
            )}
            <button
              onClick={() => setShowNewClient(true)}
              className="bg-accent hover:bg-accent-hover text-white text-sm font-medium rounded-md px-3 py-1.5 transition"
            >
              + New Client
            </button>
            <button
              onClick={() => navigate("/settings")}
              className="bg-bg-card hover:bg-bg-hover border border-border text-text text-sm rounded-md px-3 py-1.5 transition"
            >
              Settings
            </button>
            <button
              onClick={handleLogout}
              className="text-text-muted hover:text-text text-sm px-2 py-1.5 transition"
              title="Sign out"
            >
              Sign out
            </button>
          </div>
        </div>
      </header>

      {/* Client grid */}
      <main className="max-w-6xl mx-auto px-6 py-10">
        {clients === null ? (
          <p className="text-text-muted text-sm">Loading clients...</p>
        ) : clients.length === 0 ? (
          <div className="text-center py-20">
            <p className="text-text-muted">No clients yet.</p>
            <button
              onClick={() => setShowNewClient(true)}
              className="mt-4 bg-accent hover:bg-accent-hover text-white text-sm font-medium rounded-md px-4 py-2 transition"
            >
              + Add your first client
            </button>
          </div>
        ) : (
          <>
            {/* fix252: view toggle + sort control */}
            <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <div className="inline-flex rounded-md border border-border overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setView("list")}
                    className={`px-3 py-1.5 text-sm transition flex items-center gap-1.5 ${
                      view === "list"
                        ? "bg-accent/10 text-accent"
                        : "bg-bg-card text-text-muted hover:bg-bg-hover"
                    }`}
                    title="List view"
                    aria-pressed={view === "list"}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <line x1="8" y1="6" x2="21" y2="6" />
                      <line x1="8" y1="12" x2="21" y2="12" />
                      <line x1="8" y1="18" x2="21" y2="18" />
                      <line x1="3" y1="6" x2="3.01" y2="6" />
                      <line x1="3" y1="12" x2="3.01" y2="12" />
                      <line x1="3" y1="18" x2="3.01" y2="18" />
                    </svg>
                    List
                  </button>
                  <button
                    type="button"
                    onClick={() => setView("tile")}
                    className={`px-3 py-1.5 text-sm transition flex items-center gap-1.5 border-l border-border ${
                      view === "tile"
                        ? "bg-accent/10 text-accent"
                        : "bg-bg-card text-text-muted hover:bg-bg-hover"
                    }`}
                    title="Tile view"
                    aria-pressed={view === "tile"}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                      <rect x="3" y="3" width="7" height="7" rx="1" />
                      <rect x="14" y="3" width="7" height="7" rx="1" />
                      <rect x="3" y="14" width="7" height="7" rx="1" />
                      <rect x="14" y="14" width="7" height="7" rx="1" />
                    </svg>
                    Tiles
                  </button>
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm text-text-muted">
                Sort by
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value as "alphabetical" | "recent")}
                  className="bg-bg-card border border-border rounded-md px-2 py-1.5 text-sm text-text focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/20 transition"
                >
                  <option value="alphabetical">Alphabetical</option>
                  <option value="recent">Recently Added</option>
                </select>
              </label>
            </div>

            {importMsg && (
              <div className="mb-4 text-sm rounded-md border border-border bg-bg-card px-3 py-2 text-text-muted">
                {importMsg}
              </div>
            )}

            {/* fix683: two sections only — Priority (clients with the Priority
                box checked) and Clients (everyone else active). GBP/Content are
                per-client flags that don't affect placement. */}
            {([
              { key: "priority", label: "Priority" },
              { key: "clients", label: "Clients" },
            ] as const).map((section) => {
              const inSection = sortedClients.filter(
                (c) => effectivePlacement(c) === section.key
              );
              return (
                <section key={section.key} className="mb-8">
                  <div className="flex items-center gap-2 mb-3">
                    <h2 className="text-text font-semibold text-sm">
                      {section.label}
                    </h2>
                    <span className="text-text-subtle text-xs">
                      {inSection.length}
                    </span>
                  </div>

                  {inSection.length === 0 ? (
                    <p className="text-text-subtle text-sm italic px-1 py-3">
                      No clients in this section.
                    </p>
                  ) : view === "tile" ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                      {inSection.map((client) => (
                        <ClientCard
                          key={client.id}
                          client={client}
                          gbpChecked={effectiveGbp(client)}
                          contentChecked={effectiveContent(client)}
                          isPriority={effectivePriority(client)}
                          pending={pendingIds.has(client.id)}
                          activeBusy={activeBusy.has(client.id)}
                          onToggleGbp={() => toggleGbp(client)}
                          onToggleContent={() => toggleContent(client)}
                          onTogglePriority={() => togglePriority(client)}
                          onOpen={() => navigate(`/clients/${client.id}`)}
                          onDelete={() => setDeleteTarget(client)}
                          onSetActive={(a) => handleSetActive(client, a)}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="bg-bg-card border border-border rounded-lg shadow-card divide-y divide-border-subtle">
                      {inSection.map((client) => (
                        <ClientRow
                          key={client.id}
                          client={client}
                          gbpChecked={effectiveGbp(client)}
                          contentChecked={effectiveContent(client)}
                          isPriority={effectivePriority(client)}
                          pending={pendingIds.has(client.id)}
                          activeBusy={activeBusy.has(client.id)}
                          onToggleGbp={() => toggleGbp(client)}
                          onToggleContent={() => toggleContent(client)}
                          onTogglePriority={() => togglePriority(client)}
                          onOpen={() => navigate(`/clients/${client.id}`)}
                          onDelete={() => setDeleteTarget(client)}
                          onSetActive={(a) => handleSetActive(client, a)}
                        />
                      ))}
                    </div>
                  )}
                </section>
              );
            })}

            {/* fix582: Inactive section — collapsible accordion, collapsed by
                default. Sits below Other. Inactive clients live here only. */}
            {(() => {
              const inactive = sortedClients.filter(
                (c) => effectivePlacement(c) === "inactive"
              );
              return (
                <section className="mb-8">
                  <button
                    type="button"
                    onClick={() => setInactiveOpen((o) => !o)}
                    className="flex items-center gap-2 mb-3 w-full text-left group"
                    aria-expanded={inactiveOpen}
                  >
                    <span
                      className={`text-text-subtle text-xs transition-transform ${
                        inactiveOpen ? "rotate-90" : ""
                      }`}
                      aria-hidden="true"
                    >
                      ▶
                    </span>
                    <h2 className="text-text font-semibold text-sm group-hover:text-accent transition">
                      Inactive
                    </h2>
                    <span className="text-text-subtle text-xs">
                      {inactive.length}
                    </span>
                  </button>

                  {inactiveOpen &&
                    (inactive.length === 0 ? (
                      <p className="text-text-subtle text-sm italic px-1 py-3">
                        No inactive clients.
                      </p>
                    ) : view === "tile" ? (
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {inactive.map((client) => (
                          <ClientCard
                            key={client.id}
                            client={client}
                            gbpChecked={effectiveGbp(client)}
                            contentChecked={effectiveContent(client)}
                            isPriority={false}
                            pending={pendingIds.has(client.id)}
                            inactive
                            activeBusy={activeBusy.has(client.id)}
                            onToggleGbp={() => toggleGbp(client)}
                            onToggleContent={() => toggleContent(client)}
                            onTogglePriority={() => togglePriority(client)}
                            onOpen={() => navigate(`/clients/${client.id}`)}
                            onDelete={() => setDeleteTarget(client)}
                            onSetActive={(a) => handleSetActive(client, a)}
                          />
                        ))}
                      </div>
                    ) : (
                      <div className="bg-bg-card border border-border rounded-lg shadow-card divide-y divide-border-subtle">
                        {inactive.map((client) => (
                          <ClientRow
                            key={client.id}
                            client={client}
                            gbpChecked={effectiveGbp(client)}
                            contentChecked={effectiveContent(client)}
                            isPriority={false}
                            pending={pendingIds.has(client.id)}
                            inactive
                            activeBusy={activeBusy.has(client.id)}
                            onToggleGbp={() => toggleGbp(client)}
                            onToggleContent={() => toggleContent(client)}
                            onTogglePriority={() => togglePriority(client)}
                            onOpen={() => navigate(`/clients/${client.id}`)}
                            onDelete={() => setDeleteTarget(client)}
                            onSetActive={(a) => handleSetActive(client, a)}
                          />
                        ))}
                      </div>
                    ))}
                </section>
              );
            })()}
          </>
        )}
      </main>

      {/* fix268: batched Save bar for pending checkbox changes. */}
      {pendingCount > 0 && (
        <div className="fixed bottom-0 inset-x-0 bg-bg-card border-t border-border shadow-lg z-20">
          <div className="max-w-6xl mx-auto px-6 py-3 flex items-center justify-between gap-3">
            <span className="text-sm text-text-muted">
              {pendingCount} unsaved change{pendingCount === 1 ? "" : "s"}
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  setPendingGbp({});
                  setPendingContent({});
                  setPendingPriority({});
                }}
                disabled={savingSections}
                className="text-sm text-text-muted hover:text-text px-3 py-1.5 transition disabled:opacity-60"
              >
                Discard
              </button>
              <button
                onClick={handleSaveSections}
                disabled={savingSections}
                className="bg-accent hover:bg-accent-hover text-white text-sm font-medium rounded-md px-4 py-1.5 transition disabled:opacity-60"
              >
                {savingSections ? "Saving…" : "Save"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showNewClient && (
        <NewClientModal
          onClose={() => setShowNewClient(false)}
          onCreated={async (id) => {
            setShowNewClient(false);
            await refresh();
            navigate(`/clients/${id}`);
          }}
        />
      )}

      {deleteTarget && (
        <DeleteClientModal
          client={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onDeleted={async () => {
            setDeleteTarget(null);
            await refresh();
          }}
        />
      )}
    </div>
  );
}

// fix268: the mutually-exclusive GBP / Content checkboxes shown on every row.
// Clicking toggles via onToggle; the parent decides the resulting section.
// fix270: in the list view ("aligned"), each box sits in a fixed-width column so
// the GBP and Content columns line up across every row. The tile view
// ("compact") keeps the natural spacing since cards align independently.
function SectionCheckboxes({
  gbpChecked,
  contentChecked,
  onToggleGbp,
  onToggleContent,
  variant = "compact",
}: {
  gbpChecked: boolean;
  contentChecked: boolean;
  onToggleGbp: () => void;
  onToggleContent: () => void;
  variant?: "compact" | "aligned";
}) {
  const aligned = variant === "aligned";
  return (
    <div
      className={`flex items-center shrink-0 ${aligned ? "" : "gap-3"}`}
      onClick={(e) => e.stopPropagation()}
    >
      <label
        className={`flex items-center gap-1.5 text-xs text-text-muted cursor-pointer select-none ${
          aligned ? "w-16" : ""
        }`}
      >
        <input
          type="checkbox"
          checked={gbpChecked}
          onChange={onToggleGbp}
          className="accent-accent cursor-pointer"
        />
        GBP
      </label>
      <label
        className={`flex items-center gap-1.5 text-xs text-text-muted cursor-pointer select-none ${
          aligned ? "w-20" : ""
        }`}
      >
        <input
          type="checkbox"
          checked={contentChecked}
          onChange={onToggleContent}
          className="accent-accent cursor-pointer"
        />
        Content
      </label>
    </div>
  );
}

// fix301: standalone Priority checkbox shown right after the client name/URL, in
// its own fixed-width column so the boxes line up across rows. Independent of the
// Content/GBP boxes; stops click propagation so it doesn't open the client.
function PriorityCheckbox({
  checked,
  onToggle,
}: {
  checked: boolean;
  onToggle: () => void;
}) {
  return (
    <div
      className="flex items-center justify-center shrink-0 w-20"
      onClick={(e) => e.stopPropagation()}
    >
      <label className="flex items-center gap-1.5 text-xs text-text-muted cursor-pointer select-none">
        <input
          type="checkbox"
          checked={checked}
          onChange={onToggle}
          className="accent-accent cursor-pointer"
        />
        Priority
      </label>
    </div>
  );
}

function ClientCard({
  client,
  gbpChecked,
  contentChecked,
  isPriority,
  pending = false,
  inactive = false,
  activeBusy = false,
  onToggleGbp,
  onToggleContent,
  onTogglePriority,
  onOpen,
  onDelete,
  onSetActive,
}: {
  client: ClientSummary;
  gbpChecked: boolean;
  contentChecked: boolean;
  isPriority: boolean;
  pending?: boolean;
  inactive?: boolean;
  activeBusy?: boolean;
  onToggleGbp: () => void;
  onToggleContent: () => void;
  onTogglePriority: () => void;
  onOpen: () => void;
  onDelete: () => void;
  onSetActive: (active: boolean) => void;
}) {
  return (
    <div
      className={`group bg-bg-card border rounded-lg p-4 shadow-card hover:shadow-cardHover transition cursor-pointer relative ${
        pending
          ? "border-accent ring-2 ring-accent/30 bg-accent/5"
          : "border-border hover:border-border-strong"
      } ${inactive ? "opacity-75" : ""}`}
      onClick={onOpen}
    >
      {pending && (
        <span className="absolute top-2 left-2 text-[11px] font-bold uppercase tracking-wide text-white bg-accent rounded px-2 py-0.5 shadow-sm">
          Unsaved
        </span>
      )}
      <div className="flex items-start gap-3">
        <ClientAvatar client={client} />
        <div className="flex-1 min-w-0">
          <h3 className="text-text font-medium truncate">{client.businessName}</h3>
          <p className="text-text-muted text-xs truncate mt-0.5">{client.websiteUrl}</p>
          <div className="flex items-center justify-between mt-2 gap-2">
            <p className="text-text-subtle text-xs">{client.pageCount} pages</p>
            {inactive ? (
              // fix582: inactive clients show a Reactivate button instead of the
              // priority/section checkboxes.
              <div onClick={(e) => e.stopPropagation()}>
                <button
                  type="button"
                  onClick={() => onSetActive(true)}
                  disabled={activeBusy}
                  className="text-xs border border-border rounded px-2 py-1 text-text-muted hover:text-accent hover:border-accent transition disabled:opacity-60"
                >
                  {activeBusy ? "Moving…" : "↩ Reactivate"}
                </button>
              </div>
            ) : (
              <div
                className="flex items-center gap-3"
                onClick={(e) => e.stopPropagation()}
              >
                <label className="flex items-center gap-1.5 text-xs text-text-muted cursor-pointer select-none mr-2 pr-2 border-r border-border-subtle">
                  <input
                    type="checkbox"
                    checked={isPriority}
                    onChange={onTogglePriority}
                    className="accent-accent cursor-pointer"
                  />
                  Priority
                </label>
                <SectionCheckboxes
                  gbpChecked={gbpChecked}
                  contentChecked={contentChecked}
                  onToggleGbp={onToggleGbp}
                  onToggleContent={onToggleContent}
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* fix582: active clients get a "Move to Inactive" action (hover) alongside
          delete, so you can shelve rather than delete. */}
      <div className="absolute top-2 right-2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition">
        {!inactive && (
          <button
            className="text-text-subtle hover:text-warning transition px-2 py-1 text-xs"
            onClick={(e) => {
              e.stopPropagation();
              onSetActive(false);
            }}
            disabled={activeBusy}
            title="Move to Inactive"
            aria-label="Move to Inactive"
          >
            {activeBusy ? "…" : "Inactivate"}
          </button>
        )}
        <button
          className="text-text-subtle hover:text-danger transition px-2 py-1 text-sm"
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          title="Delete client"
          aria-label="Delete client"
        >
          🗑
        </button>
      </div>
    </div>
  );
}

// fix252: compact list-view row (alternative to the tile card).
function ClientRow({
  client,
  gbpChecked,
  contentChecked,
  isPriority,
  pending = false,
  inactive = false,
  activeBusy = false,
  onToggleGbp,
  onToggleContent,
  onTogglePriority,
  onOpen,
  onDelete,
  onSetActive,
}: {
  client: ClientSummary;
  gbpChecked: boolean;
  contentChecked: boolean;
  isPriority: boolean;
  pending?: boolean;
  inactive?: boolean;
  activeBusy?: boolean;
  onToggleGbp: () => void;
  onToggleContent: () => void;
  onTogglePriority: () => void;
  onOpen: () => void;
  onDelete: () => void;
  onSetActive: (active: boolean) => void;
}) {
  return (
    <div
      className={`group flex items-center gap-3 px-4 py-3 transition cursor-pointer first:rounded-t-lg last:rounded-b-lg ${
        pending
          ? "bg-accent/10 shadow-[inset_4px_0_0_0_var(--accent)]"
          : "hover:bg-bg-hover/40"
      } ${inactive ? "opacity-75" : ""}`}
      onClick={onOpen}
    >
      <ClientAvatar client={client} size="sm" />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <h3 className="text-text font-medium truncate">{client.businessName}</h3>
          {pending && (
            <span className="shrink-0 text-[11px] font-bold uppercase tracking-wide text-white bg-accent rounded px-2 py-0.5 shadow-sm">
              Unsaved
            </span>
          )}
        </div>
        <p className="text-text-muted text-xs truncate">{client.websiteUrl}</p>
      </div>
      {inactive ? (
        // fix582: inactive rows swap the checkboxes for a Reactivate button.
        <div
          className="flex items-center shrink-0"
          onClick={(e) => e.stopPropagation()}
        >
          <button
            type="button"
            onClick={() => onSetActive(true)}
            disabled={activeBusy}
            className="text-xs border border-border rounded px-2 py-1 text-text-muted hover:text-accent hover:border-accent transition disabled:opacity-60"
          >
            {activeBusy ? "Moving…" : "↩ Reactivate"}
          </button>
        </div>
      ) : (
        <>
          {/* fix301: Priority checkbox — its own fixed-width column right after the
              name/URL, vertically centered (row is items-center). Independent of the
              Content/GBP boxes. fix310: extra gap + divider so Priority reads as
              separate from the GBP/Content pair (it's a different kind of choice). */}
          <div className="flex items-center shrink-0 mr-4 pr-4 border-r border-border-subtle">
            <PriorityCheckbox checked={isPriority} onToggle={onTogglePriority} />
          </div>
          <SectionCheckboxes
            gbpChecked={gbpChecked}
            contentChecked={contentChecked}
            onToggleGbp={onToggleGbp}
            onToggleContent={onToggleContent}
            variant="aligned"
          />
        </>
      )}
      <span className="text-text-subtle text-xs shrink-0 hidden sm:inline w-20 text-right pl-6 whitespace-nowrap">
        {client.pageCount} {client.pageCount === 1 ? "page" : "pages"}
      </span>
      <div className="flex items-center shrink-0" onClick={(e) => e.stopPropagation()}>
        {!inactive && (
          <button
            className="opacity-0 group-hover:opacity-100 text-text-subtle hover:text-warning transition px-2 py-1 text-xs shrink-0"
            onClick={() => onSetActive(false)}
            disabled={activeBusy}
            title="Move to Inactive"
            aria-label="Move to Inactive"
          >
            {activeBusy ? "…" : "Inactivate"}
          </button>
        )}
        <button
          className="opacity-0 group-hover:opacity-100 text-text-subtle hover:text-danger transition px-2 py-1 text-sm shrink-0"
          onClick={() => onDelete()}
          title="Delete client"
          aria-label="Delete client"
        >
          🗑
        </button>
      </div>
    </div>
  );
}

function NewClientModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (id: number) => void;
}) {
  const [businessName, setBusinessName] = useState("");
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const trimmedName = businessName.trim();
  const trimmedUrl = websiteUrl.trim();
  const canSubmit =
    !submitting && trimmedName.length > 0 && trimmedUrl.length > 0;

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    // Client-side guards (backend re-validates either way):
    if (trimmedName.length === 0) {
      setError("Business name is required.");
      return;
    }
    if (trimmedUrl.length === 0) {
      setError("Website URL is required.");
      return;
    }
    // Normalize and check parse-ability locally so the user gets fast
    // feedback instead of a server roundtrip for obvious typos.
    const normalized = /^https?:\/\//i.test(trimmedUrl)
      ? trimmedUrl
      : `https://${trimmedUrl}`;
    try {
      const parsed = new URL(normalized);
      if (!parsed.hostname.includes(".")) {
        setError("Website URL must include a valid domain.");
        return;
      }
    } catch {
      setError("Website URL is not a valid URL.");
      return;
    }

    setSubmitting(true);
    try {
      const { id } = await createClient(trimmedName, normalized);
      onCreated(id);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create client");
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <ModalOverlay onClose={onClose}>
      <form
        onSubmit={handleSubmit}
        className="bg-bg-card border border-border rounded-lg p-5 w-full max-w-md shadow-cardHover"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-text font-semibold mb-4">New Client</h2>

        <input
          type="text"
          placeholder="Business name"
          autoFocus
          value={businessName}
          onChange={(e) => setBusinessName(e.target.value)}
          className="w-full bg-bg-card border border-border rounded-md px-3 py-2 mb-2 text-text placeholder:text-text-placeholder focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/20 transition"
        />
        <input
          type="url"
          placeholder="https://example.com"
          value={websiteUrl}
          onChange={(e) => setWebsiteUrl(e.target.value)}
          className="w-full bg-bg-card border border-border rounded-md px-3 py-2 text-text placeholder:text-text-placeholder focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/20 transition"
        />

        {error && (
          <div className="mt-3 text-sm text-danger bg-danger-bg border border-danger-border rounded-md px-3 py-2">
            {error}
          </div>
        )}

        <div className="flex justify-end gap-2 mt-4">
          <button
            type="button"
            onClick={onClose}
            className="bg-bg-card hover:bg-bg-hover border border-border text-text text-sm rounded-md px-3 py-1.5 transition"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={!canSubmit}
            className="bg-accent hover:bg-accent-hover disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium rounded-md px-3 py-1.5 transition"
          >
            {submitting ? "Creating..." : "Create"}
          </button>
        </div>
      </form>
    </ModalOverlay>
  );
}

function DeleteClientModal({
  client,
  onClose,
  onDeleted,
}: {
  client: ClientSummary;
  onClose: () => void;
  onDeleted: () => void;
}) {
  const [confirmText, setConfirmText] = useState("");
  const [submitting, setSubmitting] = useState(false);

  async function handleDelete() {
    setSubmitting(true);
    try {
      await deleteClient(client.id);
      onDeleted();
    } catch (err) {
      console.error(err);
      setSubmitting(false);
    }
  }

  return (
    <ModalOverlay onClose={onClose}>
      <div
        className="bg-bg-card border border-danger-border rounded-lg p-5 w-full max-w-md shadow-cardHover"
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="text-danger font-semibold mb-2">Delete Client</h2>
        <p className="text-text-muted text-sm mb-3">
          All content associated with{" "}
          <span className="text-text font-medium">{client.businessName}</span> will be
          permanently deleted, including pages, crawl data, research, and queue items.
        </p>
        <label className="block text-text-muted text-xs mb-1">
          Type "delete" to confirm
        </label>
        <input
          type="text"
          autoFocus
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          placeholder="delete"
          className="w-full bg-bg-card border border-danger-border rounded-md px-3 py-2 text-text placeholder:text-text-placeholder focus:outline-none focus:border-danger focus:ring-2 focus:ring-danger/20 transition"
        />

        <div className="flex justify-end gap-2 mt-4">
          <button
            onClick={onClose}
            className="bg-bg-card hover:bg-bg-hover border border-border text-text text-sm rounded-md px-3 py-1.5 transition"
          >
            Cancel
          </button>
          <button
            onClick={handleDelete}
            disabled={confirmText !== "delete" || submitting}
            className="bg-danger hover:bg-danger-hover disabled:opacity-30 disabled:cursor-not-allowed text-white text-sm font-medium rounded-md px-3 py-1.5 transition"
          >
            {submitting ? "Deleting..." : "Delete Permanently"}
          </button>
        </div>
      </div>
    </ModalOverlay>
  );
}

function ModalOverlay({
  children,
  onClose,
}: {
  children: React.ReactNode;
  onClose: () => void;
}) {
  return (
    <div
      className="fixed inset-0 bg-black/30 backdrop-blur-sm flex items-center justify-center px-4 z-50"
      onClick={onClose}
    >
      {children}
    </div>
  );
}
